import { OrderPackageAPI } from '@business/api/order';
import { PageQuery } from '@business/entities/common';
import { OrderProductId } from '@business/entities/order';
import { handleMessage, requestMessage } from '@business/messages';
import { BaseBloc } from '@core/utils/bloc';
import { Repository } from '@core/utils/repository';
import { showDeleteConfirmation } from '@modules/common';
import { ProductFormData } from '@modules/orders/ProductsPage/AddEditProductOverlay/AddEditProductOverlay';
import { switchMap } from 'rxjs/operators';
import { OrderPackageResponse, OrderPackageId, VeryCompactPackageProductResponse } from '@business/entities/order/OrderPackageResponse';

export class OrderPackageBloc extends BaseBloc {
  private readonly packagesRepo = new Repository<OrderPackageResponse>({
    getItemId: item => item.id,
  });

  readonly packages$ = this.packagesRepo.items$;
  readonly selectPackage = this.packagesRepo.selectItem;

  onReset() {
    this.packagesRepo.reset();
  }

  fetchPackagesPaginated = (query: PageQuery) => {
    return OrderPackageAPI.fetchPackagesPaginated(query);
  };

  fetchPackages = () => {
    return OrderPackageAPI.fetchPackages(false).pipe(
      this.packagesRepo.ops.reset(),
      handleMessage({ error: requestMessage('fetch_order_packages_error') }),
    );
  };

  searchPackages = (search: string, packages?: OrderPackageId[], vendorId?: string, page = 1) => {
    return OrderPackageAPI.fetchPackagesPaginated({ search, page }, packages, vendorId).pipe(
      handleMessage({ error: requestMessage('fetch_order_packages_error') }),
    );
  };

  fetchPackage = (id: OrderProductId) => {
    return OrderPackageAPI.fetchPackage(id).pipe(
      this.packagesRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_order_packages_error') }),
    );
  };

  syncPackages = () => {
    return OrderPackageAPI.syncPackages().pipe(
      this.packagesRepo.ops.upsertMany(item => ({ item })),
      handleMessage({
        type: requestMessage('sync_order_package'),
      }),
    );
  };

  createPackage = (data: Partial<ProductFormData>) => {
    return OrderPackageAPI.createPackage(data).pipe(
      this.packagesRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_order_package'),
      }),
    );
  };

  updatePackage = (id: OrderProductId, data: Partial<ProductFormData>) => {
    return OrderPackageAPI.updatePackage(id, data).pipe(
      this.packagesRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order_package'),
      }),
    );
  };

  deleteProduct = (id: OrderProductId) => {
    return showDeleteConfirmation('Delete Package', `Do you really want to remove this package?`).pipe(
      switchMap(() => OrderPackageAPI.deletePackage(id)),
      this.packagesRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_order_package'),
      }),
    );
  };

  deactivatePackage = (id: OrderProductId) => {
    return showDeleteConfirmation('Deactivate Package', `Do you really want to deactivate this package?`).pipe(
      switchMap(() => OrderPackageAPI.deletePackage(id)),
      this.packagesRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_order_package'),
      }),
    );
  };

  deletePackageProduct = (id: OrderProductId) => {
    return showDeleteConfirmation('Delete Product', `Do you really want to remove this product?`).pipe(
      switchMap(() => OrderPackageAPI.deletePackageProduct(id)),
      this.packagesRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('delete_order_package_product'),
      }),
    );
  };

  saveProducts = (id: OrderPackageId, products: VeryCompactPackageProductResponse[]) => {
    return OrderPackageAPI.saveProducts(id, products).pipe(
      this.packagesRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_order_package'),
      }),
    );
  };
}
